import React, { useEffect, useState, useRef, useContext } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../@shared/firebase/config";
import {
  getLevelFromXP,
  checkAndUpdateLevel,
  getBadgeByLevel,
} from "../../@shared/utils/Utils";
import { levelXPTable } from "../../@shared/utils/levelXPTable";
import { motion, useAnimation } from "framer-motion";
import { Box, Grid, Typography } from "@mui/material";
import { MainContext } from "../../App";

// Dummy points for test mode
const dummyPoints = {
  damageDealtPoints: 100,
  eliminationPoints: 50,
  placementPoints: 300,
  playedCardPoints: 100,
  discardCardPoints: 50,
  tradeCardPoints: 75,
};

const containerStyle = {
  padding: "15px",
  alignItems: "center",
  backgroundColor: "#16116e",
  borderRadius: "10px",
  width: "100%",
  position: "relative",
  userSelect: "none",
  cursor: "pointer",
};

const titleStyle = {
  fontSize: "24px",
  fontWeight: "bold",
  color: "#fff",
  marginBottom: "15px",
  textAlign: "center",
};

const progressWrapperStyle = {
  width: "100%",
  marginBottom: "15px",
};

const progressBarContainerStyle = {
  width: "100%",
  height: "20px",
  backgroundColor: "rgba(0,0,0,0.4)",
  borderRadius: "10px",
  overflow: "hidden",
};

const progressBarStyle = {
  height: "100%",
  backgroundColor: "#fbcf57",
  borderRadius: "10px",
};

const progressLabelsStyle = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  marginTop: "5px",
};

const progressLabelStyle = {
  color: "#ccc",
  fontSize: "14px",
};

const breakdownContainerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "30px",
  marginBottom: "10px",
};

const breakdownTextStyle = {
  color: "#fff",
  fontSize: "16px",
  textAlign: "center",
  fontWeight: "bold",
  textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  3px 0 #000`,
};

const totalStyle = {
  fontSize: "24px",
  fontWeight: "bold",
  color: "#fbcf57",
  textAlign: "center",
  textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  5px 0 #000`,
};

const levelUpBubbleStyle = {
  position: "absolute",
  top: "-20px",
  backgroundColor: "#fbcf57",
  padding: "10px",
  borderRadius: "12px",
  boxShadow: "0px 2px 3px rgba(0,0,0,0.3)",
};

const levelUpTextStyle = {
  fontWeight: "bold",
  fontSize: "18px",
  color: "#16116e",
};

const XPBreakdown = ({
  userData,
  allPlayersXP,
  playerPoints,
  roomId,
  test = false,
}) => {
  const { Theme } = useContext(MainContext);
  const [xpBreakdown, setXpBreakdown] = useState(null);
  const [xpTotal, setXpTotal] = useState(0);
  const [levelInfo, setLevelInfo] = useState({});
  const [displayedTotal, setDisplayedTotal] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [skip, setSkip] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [progressRange, setProgressRange] = useState({ min: 0, max: 100 });

  console.log("XPBreakdown component rendered", userData);
  console.log("XPBreakdown component rendered", allPlayersXP);
  console.log("XPBreakdown component rendered", playerPoints);

  // For the breakdown text fade & slide
  const breakdownControls = useAnimation();

  // Hold the current total as a mutable ref
  const displayedTotalRef = useRef(0);

  const formatPointLabel = (key) => {
    const map = {
      eliminationPoints: "Eliminations",
      damageDealtPoints: "Damage Dealt",
      tradeCardPoints: "Trades",
      playedCardPoints: "Cards Played",
      discardCardPoints: "Cards Discarded",
      placementPoints: "Placement",
    };
    return map[key] || key;
  };

  const getTotalXP = (pointsObj) => {
    if (!pointsObj) return 0;
    return Object.entries(pointsObj)
      .filter(([key]) => key !== "questStats")
      .reduce((total, [_, val]) => total + (val || 0), 0);
  };

  return (
    <Grid
      item
      container
      direction={"column"}
      xs={12}
      style={{
        justifyContent: "center",
        alignItems: "center",
        height: "80%",
      }}
    >
      <Grid
        item
        container
        direction={"column"}
        xs={12}
        style={{
          justifyContent: "center",
          alignItems: "center",
          height: "fit-content",
        }}
      >
        <Typography
          variant="h4"
          style={{
            color: "white",
            textAlign: "center",
            marginBottom: "10px",
            fontWeight: 900,
            textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  5px 0 #000`,
          }}
        >
          XP Breakdown
        </Typography>

        <Box
          xs={12}
          style={{
            display: "flex",
            position: "relative",
            justifyContent: "center",
            alignItems: "center",
            height: "fit-content",
          }}
        >
          <img
            src={getBadgeByLevel(userData.level).badge}
            style={{
              height: 120,
              width: "auto",
              //   transform: iconTop ? "translate(-50%, 0%)" : "",
            }}
          />
          <Typography
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: { xs: "25px", md: "35px" },
              fontWeight: "bold",
              color: "#fff",
              textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  3px 0 #000`,
            }}
          >
            {userData.level}
          </Typography>
        </Box>

        <>
          {(() => {
            const currentLevel = parseInt(userData.level || "1", 10);
            const currentXP = userData.xp || 0;

            const currentLevelData = levelXPTable.find(
              (lvl) => parseInt(lvl.level, 10) === currentLevel
            );
            const nextLevelData = levelXPTable.find(
              (lvl) => parseInt(lvl.level, 10) === currentLevel + 1
            );

            const xpIntoLevel =
              currentXP - (currentLevelData?.cumulativeXP || 0);
            const xpNeeded = nextLevelData
              ? nextLevelData.cumulativeXP - currentLevelData.cumulativeXP
              : 1;

            const progressPercent = Math.min(
              100,
              Math.max(0, Math.round((xpIntoLevel / xpNeeded) * 100))
            );

            return (
              <>
                <div
                  style={{
                    width: "80%",
                    height: "16px",
                    borderRadius: "10px",
                    border: "2px solid #000",
                    backgroundColor: Theme.palette.text.main,
                    zIndex: 30,
                    marginTop: 10,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progressPercent}%`,
                      backgroundColor: Theme.palette.border.dark,
                      transition: "width 0.3s ease-in-out",
                    }}
                  />
                </div>
                <Typography
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: 8,
                    color: "#fff",
                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                    zIndex: 31,
                    marginTop: "-5px",
                    marginBottom: 2,
                  }}
                >
                  {progressPercent}%
                </Typography>
              </>
            );
          })()}
        </>

        {Object.entries(playerPoints?.[userData.id] || {})
          .filter(([key]) => key !== "questStats") // Skip questStats
          .sort(([, aVal], [, bVal]) => bVal - aVal)
          .map(([key, value], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.2 }}
              style={breakdownContainerStyle}
            >
              <Typography style={breakdownTextStyle}>
                {formatPointLabel(key)}: {value > 0 && "+"}
                <span
                  style={{ color: value > 0 && Theme.palette.secondary.main }}
                >
                  {value}
                </span>{" "}
                XP
              </Typography>
            </motion.div>
          ))}

        {/* Final Total XP */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2 }}
          style={{ marginTop: "15px" }}
        >
          <Typography style={totalStyle}>
            Total XP Earned: <br />+{getTotalXP(playerPoints?.[userData.id])} XP
          </Typography>
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default XPBreakdown;
